import { Box, Divider, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../components/CustomButton";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { apiUrl } from "../../../config/settings";
import Lottie from "lottie-react";
import loadingButton from "../../../assets/loadingButton.json";
import { useEffect, useState } from "react";
import { MINI_SITE_TYPE_ID } from "../../../config/const";
import ClientItems from "./ClientItems";
import CheckClient from "./CheckClient";
import ClientDetails from "./ClientDetails";
import { useParams } from "react-router-dom";
import NullCheck from "../../../components/NullCheck";
import { setShowLoading } from "../../../store/sysSlice";
import ServiceSummaryList from "./ServiceSummaryList";
import CustomDialog from "../../../components/CustomDialog";
import Media from "./Media";
import useForms from "../../../hooks/useForms";

const ServicesCall = ({ bgColor }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation();
  const { formData } = useForms()
  const { clientGuid } = useParams();
  const data = useSelector((state) => state?.biz?.data);
  const { handleSubmit, control, reset, watch, clearErrors, formState: { errors }, } = useForm();
  const [loading, setLoading] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [itemList, setItemList] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [error, setError] = useState(null);
  const [media1, setMedia1] = useState(null);
  const [media2, setMedia2] = useState(null);
  const clientId = watch("clientId");

  useEffect(() => {
    if (data?.client?.id) {
      setClient(data?.client);
    }
    else if (clientGuid && data?.biz?.id) {
      getClient(clientGuid);
    }
  }, [clientGuid, data]);

  const getClient = async (clientGuid) => {
    if (!clientId && !clientGuid) return;

    try {
      dispatch(setShowLoading(true));
      const id = clientId || clientGuid;
      const res = await axios.get(`${apiUrl}/miniSite/checkClient/${data?.biz?.id}/${id}`)
      dispatch(setShowLoading(false));
      setClient(res?.data?.data);
    }
    catch (error) {
      dispatch(setShowLoading(false));
      setError("לקוח לא נמצא")
      console.log("error", error);
    }
  };

  const setClient = (client) => {
    if (client?.items?.length === 0) setItemList([""])

    const obj = {
      ...client,
      name: !data?.miniSite?.hideClientDetails ? client?.contactName : "",
      tel: !data?.miniSite?.hideClientDetails ? client?.tel : "",
      clientId: client.guid
    }
    reset(obj);
    setClientData(obj);
  }

  const onSubmit = async (values) => {
    try {
      if (itemList.length === 0) return alert("יש להוסיף לפחות ציוד אחד לרשימה");

      setLoading(true);
      const obj = {
        isRead: false,
        employeeId: data?.emp?.id,
        department: data?.emp?.department,
        clienCompany: clientData?.company,
        clientId: values.id ?? "",
        contentWithItems: itemList,
        ...values,
      };

      const res = await axios.post(`${apiUrl}/servicesCall`, obj)

      if (media1) {
        await axios.post(`${apiUrl}/servicesCall/media/${res?.data?.callId}`, formData({ file: media1 }))
      }

      if (media2) {
        await axios.post(`${apiUrl}/servicesCall/media/${res?.data?.callId}`, formData({ file: media2 }))
      }

      setSucceeded(true);
      setClientData(null);
      reset({ name: "", clientId: "", tel: "", addr: "", content: "", employeeId: "" });

      const timer = setTimeout(() => {
        setLoading(false);
        setSucceeded(false);
        setItemList([]);
        if (clientGuid) getClient(clientGuid);
        if (data?.client?.guid) getClient(clientGuid);
        if (data?.client?.guid) getClient(data?.client?.guid);
        clearTimeout(timer);
      }, 3000);

    }
    catch (error) {
      alert("אירוע שגיאה, נסה שנית");
      setLoading(false);
    };
  }

  const onSelectTab = (event, value) => {
    setSelectedTab(value)
  }

  if (loading) {
    return (
      <Stack
        width="100%"
        height="100%"
        justifyContent="center"
        alignItems="center"
      >
        <Stack
          width={250}
          height={250}
          justifyContent="center"
          alignItems="center"
        >
          <Lottie animationData={loadingButton} loop={true} />
          {succeeded ? (
            <Typography fontWeight="bold">
              {t("SEND_SERVICES_SUCCEEDED")}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    );
  }

  return (
    <NullCheck valueForCheck={data?.miniSite?.type !== MINI_SITE_TYPE_ID.PORTAL}>
      <Stack minHeight='50vh' padding={1} bgcolor={bgColor ?? "secondary.main"}>

        <Stack height='100%' border={`2px solid black`}>
          {clientData?.serviceList?.length > 0
            ? <Tabs value={selectedTab} onChange={onSelectTab}>
              <Tab label={t("SERVICES_CALLS")} />
              <Tab label={`${t("SERVICES_SUMMARY_LIST")}`} />
            </Tabs>
            : null}

          <Divider sx={{ bgcolor: "secondary.light" }} />

          <form style={{ display: selectedTab !== 0 ? 'none' : 'block' }} onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={1}>
              <Stack padding={1} gap={2} justifyContent="center">

                <NullCheck valueForCheck={!clientData}>
                  <CheckClient
                    clientData={clientData}
                    control={control}
                    errors={errors}
                    getClient={getClient}
                  />
                </NullCheck>

                <NullCheck valueForCheck={clientData}>
                  <ClientDetails
                    control={control}
                    errors={errors}
                    data={data}
                  />

                  <ClientItems
                    itemList={itemList}
                    setItemList={setItemList}
                    clientData={clientData}
                  />
                </NullCheck>
              </Stack>

              <NullCheck valueForCheck={clientData && data?.biz?.hasServiceMedia}>
                <Media
                  setMedia1={setMedia1}
                  setMedia2={setMedia2}
                  clearErrors={clearErrors}
                />
              </NullCheck>

              <NullCheck valueForCheck={clientData}>
                <Stack padding={1}>
                  <CustomButton loading title="SEND" submit />
                </Stack>
              </NullCheck>

            </Stack>
          </form>

          <Box style={{ display: selectedTab !== 1 ? 'none' : 'block' }}>
            <ServiceSummaryList clientData={clientData} />
          </Box>

        </Stack>

      </Stack>

      {error
        ? <CustomDialog
          title="ERROR"
          min
          open={error ? true : false}
          onClose={() => setError(null)}
        >
          <Stack padding={2} alignItems='center' minWidth={250}>
            <Typography color='error' variant="h6" fontWeight='bold'>
              {error}
            </Typography>
          </Stack>
        </CustomDialog>
        : null
      }
    </NullCheck>
  );
};
export default ServicesCall;
